import { getTradingIntent } from "features/onboarding/guided/intent";
import { generatePath, matchPath, Redirect } from "react-router-dom";
import { Location } from "history";
import React from "react";
import {
  ACCOUNT_EXTRA_DETAILS_SUFFIX,
  ACCOUNT_EXTRA_DETAILS_TABS,
  ADMIN_PATH,
  ADMIN_TRADING_CONFIGURATION_FORM_PATH,
  AFFILIATION_PATH,
  BACKTESTING_EXTRA_DETAILS_SUFFIX,
  BASE_BILLING_SETTINGS_PATH,
  BASE_BOTS_WITH_ACTION_PATH,
  BASE_CANCELLED_AGREEMENT_PATH,
  BASE_FAILED_AGREEMENT_PATH,
  BASE_PAID_AGREEMENT_PATH,
  BASE_PROCESSED_AGREEMENT_PATH,
  BASE_PUBLIC_BOT_PATH,
  BASE_PUBLIC_EDIT_BOT_PATH,
  BOT_EXTRA_DETAILS_SUFFIX,
  COIN_EXTRA_DETAILS_SUFFIX,
  CONNECT_TELEGRAM_PATH,
  CONNECT_WHOP_CHANNEL_PATH,
  CREATE_ACCOUNT_PATH,
  CREATE_SIGNAL_PATH,
  DISCORD_DISCONNECT_CONFIRMED_PATH,
  DISCORD_FOLLOW_SIGNAL_PATH,
  EXTERNAL_ACCOUNT_CONFIGURATION_PATH,
  GROUP_MANAGEMENT_PATH,
  GROUP_MANAGEMENT_TABS,
  IS_WHOP,
  LOGIN_PATH,
  MARKETPLACE_GROUP_SUFFIX,
  NEXT_URL_QUERY_PARAM,
  OAUTH_LOGIN_PATH,
  ONBOARDING_CONNECT_EXCHANGE_PATH,
  ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
  ONBOARDING_COPY_DCA_BOT_PATH,
  ONBOARDING_DCA_BOT_OVERVIEW_PATH,
  ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH,
  ONBOARDING_PATHS,
  ONBOARDING_SELECT_EXCHANGE_PATH,
  ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH,
  ONBOARDING_TRADING_METHOD_PATH,
  ONBOARDING_VERIFY_EMAIL_PATH,
  PATH_TO_FEATURE_FLAG,
  PORTFOLIO_ACCOUNTS_PATH,
  QUICK_ONBOARDING_PATH,
  QUICK_ONBOARDING_SUFFIX,
  SECURITY_SETTINGS_PATH,
  SETTINGS_PATH,
  SIGNAL_EXTRA_DETAILS_PATH,
  SIGNAL_EXTRA_DETAILS_SUFFIX,
  TELEGRAM_DISCONNECT_CONFIRMED_PATH,
  TRADE_EXTRA_DETAILS_SUFFIX,
  VALIDATE_TELEGRAM_PATH,
  WHOP_CHANNEL_ERROR_PATH,
  WHOP_EXPERIENCE_DEFAULT_PATH,
} from "common/constants";
import { FixMeLater } from "types";
import RedirectWithQuery from "common/Routes/RedirectWithQuery";
import { FILTER_TYPES_DATA } from "@shared/features/filters/constants";
import {
  AuthUser,
  BotType,
  FilterType,
  TelegramWebappQueryData,
  WorkspaceType,
} from "@shared/types";
import { workspaceToDefaultPath } from "common/Home/General/constants.tsx";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import localStorageService from "utils/localStorageService";
import {
  CONNECT_TELEGRAM_KEY,
  ONBOARDING_STEP_KEY,
} from "@shared/features/onboarding/constants";
import {
  getOnboardingLocalStorageValue,
  getOnboardingResumePath,
} from "features/onboarding/common/utils";
import {
  BINANCE_AUTH_PATH,
  BINGX_AUTH_PATH,
  BITGET_AUTH_PATH,
  BOT_TYPE_TO_QUERY_BOT_TYPE,
  BYBIT_AUTH_PATH,
  ENV_TYPE_QUERY_PARAM,
  EXTERNAL_ACCOUNT_INVITE_CLIENT_TOKEN_QUERY_PARAM,
  KUCOIN_AUTH_PATH,
  MEXC_AUTH_PATH,
  OKX_AUTH_PATH,
  OKX_EU_AUTH_PATH,
  REGISTRATION_REF_URL_KEY,
  SIGNAL_EXTRA_DETAILS_TABS,
  SIGNAL_TARGET_AUTO_TRADING,
  SIGNAL_TARGET_URL_KEY,
  SIGNAL_URL_KEY,
  SUB_GROUP_URL_KEY,
} from "@shared/common/constants";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import {
  APPLIED_BACKTESTING_QUERY_PARAM,
  BACKTESTING_BOT_QUERY_PARAM,
  BOT_RANK_QUERY_PARAM,
  NEW_BOT_ID_QUERY_PARAM,
  SIGNAL_BOTS_SIGNALS_PATH,
} from "features/bots/common/constants";
import { getStoreWorkspace } from "@shared/common/utils";
import useQuery from "utils/hooks/useQuery";
import {
  QUICK_ONBOARDING_KEY,
  TELEGRAM_VIEW_SIGNAL_KEY,
} from "features/quickOnboarding/constants";
import {
  getRedirectConnectWhopChannel,
  getRedirectWhopCreateBot,
} from "@shared/utils/hooks/misc.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import {
  getChannelEntryPath,
  isChannelEntry,
} from "features/telegram/utils.ts";

const removeTrailingSlash = (location: string) => {
  return location.replace(/\/$/, "");
};

const getRoute = (
  location: string,
  path: string,
  params: Record<string, number | string | undefined>
) => {
  const baseLocation = getRouteWithoutPath(location, path);
  return removeTrailingSlash(baseLocation) + generatePath(path, params);
};

export const getCoinExtraDetailsRoute = (
  location: string,
  coin: string,
  tab?: string
) => {
  return getRoute(location, COIN_EXTRA_DETAILS_SUFFIX, { coin, tab });
};

export const getAccountExtraDetailsRoute = (
  location: string,
  accountId: number,
  tab?: string
) => {
  return getRoute(location, ACCOUNT_EXTRA_DETAILS_SUFFIX, {
    accountId,
    tab: tab ?? ACCOUNT_EXTRA_DETAILS_TABS.GENERAL,
  });
};

export const getBotExtraDetailsRoute = (
  location: string,
  botId: string | number,
  tab?: string
) => {
  return getRoute(location, BOT_EXTRA_DETAILS_SUFFIX, {
    botId,
    tab,
  });
};

export const getBacktetingExtraDetailsRoute = (
  location: string,
  botId: string | number,
  tab?: string
) => {
  return getRoute(location, BACKTESTING_EXTRA_DETAILS_SUFFIX, {
    botId,
    tab,
  });
};

export const getQuickOnboardingRoute = (location: string) => {
  return getRoute(location, QUICK_ONBOARDING_SUFFIX, {});
};

export const getGroupExtraDetailsRoute = (
  location: string,
  subGroupId: string,
  tab?: string
) =>
  getRoute(location, MARKETPLACE_GROUP_SUFFIX, {
    subGroupId,
    tab,
  });

export const getBotFormBacktestingQuery = (
  backtestBotId: number,
  backtestId: number
) => {
  return {
    [BACKTESTING_BOT_QUERY_PARAM]: backtestBotId.toString(),
    [APPLIED_BACKTESTING_QUERY_PARAM]: backtestId.toString(),
  };
};

export const getBotFormBacktestingRoute = (
  location: string,
  backtestBotId: number,
  backtestId: number
) => {
  const query = getBotFormBacktestingQuery(backtestBotId, backtestId);
  const updatedQuery = new URLSearchParams();
  Object.entries(query).forEach(([queryKey, value]) => {
    updatedQuery.set(queryKey, value);
  });
  return `${location}?${updatedQuery.toString()}`;
};

export const getBotFormRoute = (
  botType: BotType,
  botFormType:
    | BotFormType.CREATE
    | BotFormType.EDIT
    | BotFormType.DUPLICATE
    | BotFormType.BULK_CREATE,
  botId?: number,
  isChannelConfiguration?: boolean,
  backtestId?: number
) => {
  const baseRoute = generatePath(
    isChannelConfiguration
      ? ADMIN_TRADING_CONFIGURATION_FORM_PATH
      : BASE_BOTS_WITH_ACTION_PATH,
    {
      botId,
      action: botFormType,
      botType: BOT_TYPE_TO_QUERY_BOT_TYPE[botType],
    }
  );
  if (backtestId) {
    const updatedQuery = new URLSearchParams();
    updatedQuery.set(APPLIED_BACKTESTING_QUERY_PARAM, backtestId.toString());
    return `${baseRoute}?${updatedQuery.toString()}`;
  }
  return baseRoute;
};

export const getPublicBotPath = (
  botType: BotType,
  botUuid: string,
  botRank?: number,
  addWorkspace?: boolean
) => {
  let path = generatePath(BASE_PUBLIC_BOT_PATH, {
    botType: BOT_TYPE_TO_QUERY_BOT_TYPE[botType],
    botUUID: botUuid,
  });
  const searchParams = new URLSearchParams();
  if (botRank !== undefined) {
    searchParams.set(BOT_RANK_QUERY_PARAM, botRank.toString());
  }
  if (addWorkspace && getStoreWorkspace() === WorkspaceType.DEMO) {
    searchParams.set(ENV_TYPE_QUERY_PARAM, WorkspaceType.DEMO);
  }
  const searchParamsStr = searchParams.toString();
  if (searchParamsStr) {
    path += `?${searchParamsStr}`;
  }
  return path;
};

export const getPublicBotEditPath = (botType: BotType, botUuid: string) => {
  return generatePath(BASE_PUBLIC_EDIT_BOT_PATH, {
    botType: BOT_TYPE_TO_QUERY_BOT_TYPE[botType],
    botUUID: botUuid,
  });
};

export const getEditBotRoute = (botType: BotType, botId?: number) => {
  return getBotFormRoute(botType, BotFormType.EDIT, botId);
};

export const getDuplicateBotRoute = (botType: BotType, botId: number) => {
  return getBotFormRoute(botType, BotFormType.DUPLICATE, botId);
};

export const getCreateBotRoute = (botType: BotType, isBulk?: boolean) => {
  return getBotFormRoute(
    botType,
    isBulk ? BotFormType.BULK_CREATE : BotFormType.CREATE
  );
};

export const getTradeExtraDetailsRoute = (
  location: string,
  tradeId: number,
  tab?: string,
  isEdit?: boolean
) => {
  return (
    removeTrailingSlash(location) +
    generatePath(TRADE_EXTRA_DETAILS_SUFFIX, {
      tradeId,
      tab,
      edit: isEdit ? "edit" : undefined,
    })
  );
};

export const getSignalExtraDetailsRoute = (
  location: string,
  signalId: number,
  tab: string,
  isEdit?: boolean
) => {
  return (
    removeTrailingSlash(location) +
    generatePath(SIGNAL_EXTRA_DETAILS_SUFFIX, {
      signalId,
      tab,
      edit: isEdit ? "edit" : undefined,
    })
  );
};

export const appendSubGroupId = (path: string, subGroupId: number) => {
  const query = new URLSearchParams();
  query.set(
    FILTER_TYPES_DATA[FilterType.GROUPS].queryKey,
    JSON.stringify([subGroupId])
  );
  return `${path}?${query.toString()}`;
};

export const getGroupManagementRoute = (tab?: string, subGroupId?: number) => {
  const path = generatePath(GROUP_MANAGEMENT_PATH, {
    tab: tab || GROUP_MANAGEMENT_TABS.USERS.toLowerCase(),
  });
  return subGroupId ? appendSubGroupId(path, subGroupId) : path;
};

export const getRouteWithoutPath = (location: string, pathToRemove: string) => {
  const match: FixMeLater = matchPath(location, {
    path: pathToRemove,
    exact: false,
    strict: false,
  });
  return match?.params?.[0] ?? location;
};

const isInPaths = (location: Location, paths: string[]) => {
  for (let path of paths) {
    if (
      matchPath(location.pathname, {
        path,
        exact: false,
        strict: false,
      })
    ) {
      return true;
    }
  }
  return false;
};

export const isBypassPath = (location: Location): boolean => {
  return isInPaths(location, [SETTINGS_PATH, AFFILIATION_PATH, ADMIN_PATH]);
};

export const isOnboardingPath = (location: Location): boolean => {
  return isInPaths(location, ONBOARDING_PATHS);
};

// A user still onboarding who came from a channel post's button is in the
// platform (the bot form) once past the onboarding steps.
export const isChannelEntryInPlatform = (location: Location): boolean => {
  return isChannelEntry() && !isOnboardingPath(location);
};

let didWhopRedirect = false;

const checkWhopRedirects = (
  user: AuthUser,
  testPath: (path: string, exact?: boolean) => boolean,
  workspace?: WorkspaceType
) => {
  if (!IS_WHOP) return;
  if (testPath(WHOP_EXPERIENCE_DEFAULT_PATH, false)) {
    return (
      <RedirectWithQuery
        to={workspaceToDefaultPath[workspace ?? WorkspaceType.USER]}
      />
    );
  }

  if (!user.isWhopExperience()) return;

  if (getRedirectConnectWhopChannel(user)) {
    if (!testPath(CONNECT_WHOP_CHANNEL_PATH)) {
      return <RedirectWithQuery to={CONNECT_WHOP_CHANNEL_PATH} />;
    }
    return;
  }
  if (
    !user.is_whop_creator &&
    !user.has_connected_whop_groups &&
    !didWhopRedirect
  ) {
    if (!testPath(WHOP_CHANNEL_ERROR_PATH)) {
      didWhopRedirect = true;
      return <RedirectWithQuery to={WHOP_CHANNEL_ERROR_PATH} />;
    }
    return;
  }
  if (getRedirectWhopCreateBot(user) && !didWhopRedirect) {
    didWhopRedirect = true;
    return <RedirectWithQuery to={getCreateBotRoute(BotType.SIGNAL)} />;
  }
};

const checkOnboardingRedirects = (
  user: AuthUser,
  testPath: (path: string, exact?: boolean) => boolean,
  query: URLSearchParams,
  workspace?: WorkspaceType
) => {
  const bypassPaths = [SETTINGS_PATH, AFFILIATION_PATH];
  if (user.is_admin) {
    bypassPaths.push(ADMIN_PATH);
  }

  if (
    user.isWhopExperience() ||
    user.isExpired() ||
    bypassPaths.some((path) => testPath(path, false)) ||
    // From a channel post's button: the platform's channel flow, not
    // onboarding, for this session.
    isChannelEntry()
  ) {
    return null;
  }

  const isOnboardingPath = ONBOARDING_PATHS.some((path) =>
    testPath(path, false)
  );
  if (user.is_sub_user) {
    if (
      isOnboardingPath &&
      !query.get(EXTERNAL_ACCOUNT_INVITE_CLIENT_TOKEN_QUERY_PARAM)
    ) {
      return <RedirectWithQuery to="/" />;
    }
    return null;
  }

  const step = getOnboardingLocalStorageValue()?.[user.id];
  const queryExchange = query.get("exchange");

  if (
    (isOnboardingPath &&
      (!user.is_onboarding || step === OnboardingDetailedStep.SKIP)) ||
    (user.is_sub_user &&
      !query.get(EXTERNAL_ACCOUNT_INVITE_CLIENT_TOKEN_QUERY_PARAM))
  )
    return <RedirectWithQuery to="/" />;

  if (user.is_onboarding && !query.get(NEW_BOT_ID_QUERY_PARAM)) {
    if (!user.is_email_verified && user.email) {
      if (!testPath(ONBOARDING_VERIFY_EMAIL_PATH)) {
        return <RedirectWithQuery to={ONBOARDING_VERIFY_EMAIL_PATH} />;
      }
    } else if (
      (!user.has_clients_connected || testPath(BITGET_AUTH_PATH)) &&
      !(
        workspace === WorkspaceType.DEMO &&
        query.get("guided") === "1" &&
        query.get("setup") !== "1" &&
        getTradingIntent(query) &&
        (testPath(ONBOARDING_TRADING_METHOD_PATH) ||
          testPath(ONBOARDING_COPY_DCA_BOT_PATH))
      ) &&
      !testPath(ONBOARDING_SELECT_EXCHANGE_PATH) &&
      (!testPath(ONBOARDING_CONNECT_EXCHANGE_PATH) || queryExchange === null)
    ) {
      return <RedirectWithQuery to={ONBOARDING_SELECT_EXCHANGE_PATH} />;
    } else if (user.has_clients_connected) {
      if (!isOnboardingPath && step !== OnboardingDetailedStep.SKIP) {
        const savedPath = getOnboardingResumePath(user.id);
        const isConnectionStep = [
          OnboardingDetailedStep.SELECT_EXCHANGE,
          OnboardingDetailedStep.CONNECT_EXCHANGE,
        ].includes(step);
        if (savedPath || isConnectionStep) {
          const savedQuery = new URLSearchParams(savedPath?.split("?")[1]);
          const target =
            isConnectionStep && user.has_valid_clients_connected
              ? `${ONBOARDING_TRADING_METHOD_PATH}?${savedQuery}`
              : savedPath;
          if (target) return <RedirectWithQuery to={target} />;
        }
      }
      if (
        testPath(ONBOARDING_SELECT_EXCHANGE_PATH) &&
        query.get("startMode") === "1" &&
        query.get("guided") !== "1" &&
        query.has("method") &&
        getTradingIntent(query)
      )
        return;
      if (step) {
        if (
          step === OnboardingDetailedStep.COPY_DCA_BOT &&
          !testPath(ONBOARDING_COPY_DCA_BOT_PATH)
        )
          return <RedirectWithQuery to={ONBOARDING_COPY_DCA_BOT_PATH} />;
        else if (
          step === OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP &&
          !testPath(ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH)
        )
          return (
            <RedirectWithQuery to={ONBOARDING_FOLLOW_SIGNALS_GROUP_PATH} />
          );
        else if (
          step === OnboardingDetailedStep.CONNECT_EXTERNAL_ACCOUNT &&
          !testPath(ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH)
        )
          return (
            <RedirectWithQuery to={ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH} />
          );
        else if (
          step === OnboardingDetailedStep.SIGNALS_GROUP_OVERVIEW &&
          !testPath(ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH)
        )
          return (
            <RedirectWithQuery to={ONBOARDING_SIGNALS_GROUP_OVERVIEW_PATH} />
          );
        else if (
          step === OnboardingDetailedStep.DCA_BOT_OVERVIEW &&
          !testPath(ONBOARDING_DCA_BOT_OVERVIEW_PATH)
        )
          return <RedirectWithQuery to={ONBOARDING_DCA_BOT_OVERVIEW_PATH} />;
        else if (
          (OnboardingDetailedStep.TRADING_METHOD === step ||
            (IS_TELEGRAM_WEB_APP &&
              !(
                workspace === WorkspaceType.DEMO &&
                query.get("guided") === "1" &&
                query.get("prepareDemo") === "1"
              ) &&
              [
                OnboardingDetailedStep.CONNECT_EXCHANGE,
                OnboardingDetailedStep.SELECT_EXCHANGE,
              ].includes(step))) && // Handle redirect when polling for new accounts in telegram webapps
          !testPath(ONBOARDING_TRADING_METHOD_PATH)
        )
          return <RedirectWithQuery to={ONBOARDING_TRADING_METHOD_PATH} />;
      } else if (!isOnboardingPath) {
        return <RedirectWithQuery to={ONBOARDING_TRADING_METHOD_PATH} />;
      }
    }
  }
};

const checkConnectTelegramRedirects = (
  user: AuthUser,
  testPath: (path: string, exact?: boolean) => boolean,
  query: URLSearchParams
) => {
  if (
    !user.is_telegram_connected &&
    ((testPath(CONNECT_TELEGRAM_PATH) && query.get("short_uuid")) ||
      (testPath(VALIDATE_TELEGRAM_PATH) &&
        query.get("short_uuid") &&
        query.get("sec_code")))
  ) {
    if (
      user.is_onboarding &&
      !localStorageService.getValue(ONBOARDING_STEP_KEY) &&
      !testPath(ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH)
    ) {
      return (
        <RedirectWithQuery to={ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH} />
      );
    } else if (
      !user.is_onboarding &&
      !localStorageService.getValue(CONNECT_TELEGRAM_KEY) &&
      !testPath(SECURITY_SETTINGS_PATH)
    ) {
      return <RedirectWithQuery to={SECURITY_SETTINGS_PATH} />;
    }
  }

  const connectTelegramOriginPath =
    localStorageService.getValue(CONNECT_TELEGRAM_KEY);

  if (connectTelegramOriginPath) {
    if (user.is_onboarding) {
      localStorageService.removeValue(CONNECT_TELEGRAM_KEY);
    } else {
      const parsedValue = JSON.parse(connectTelegramOriginPath);
      if (!testPath(parsedValue.pathname)) {
        return (
          <RedirectWithQuery
            to={`${parsedValue.pathname}${parsedValue.search}`}
          />
        );
      }
    }
  }

  if (
    testPath(TELEGRAM_DISCONNECT_CONFIRMED_PATH) ||
    testPath(DISCORD_DISCONNECT_CONFIRMED_PATH)
  ) {
    return <RedirectWithQuery to={SECURITY_SETTINGS_PATH} />;
  }
};

export const checkQuickConnectRedirects = (pathname: string) => {
  return [
    BINANCE_AUTH_PATH,
    BYBIT_AUTH_PATH,
    OKX_AUTH_PATH,
    OKX_EU_AUTH_PATH,
    BITGET_AUTH_PATH,
    KUCOIN_AUTH_PATH,
    BINGX_AUTH_PATH,
    MEXC_AUTH_PATH,
  ].some((path) =>
    matchPath(pathname, {
      path,
      exact: true,
      strict: false,
    })
  );
};

const checkExternalLinksRedirects = (
  user: AuthUser,
  location: Location,
  query: URLSearchParams
) => {
  const _matchPath = (path: string, exact = true) =>
    matchPath(location.pathname, {
      path,
      exact,
      strict: false,
    });
  const accountConfigurationMatch = _matchPath(
    EXTERNAL_ACCOUNT_CONFIGURATION_PATH
  ) as { params?: { accountId?: string } };
  if (accountConfigurationMatch?.params?.accountId) {
    const accountConfigurationPath = getAccountExtraDetailsRoute(
      PORTFOLIO_ACCOUNTS_PATH,
      Number(accountConfigurationMatch.params.accountId)
    );
    return <RedirectWithQuery to={accountConfigurationPath} />;
  }

  if (checkQuickConnectRedirects(location.pathname)) {
    if (user.is_onboarding) {
      return <RedirectWithQuery to={ONBOARDING_CONNECT_EXCHANGE_PATH} />;
    } else return <RedirectWithQuery to={CREATE_ACCOUNT_PATH} />;
  }

  if (_matchPath(DISCORD_FOLLOW_SIGNAL_PATH)) {
    const { signalId, subGroupId, target } = getSignalFollowData(query);
    if (signalId && subGroupId) {
      if (user.is_discord_connected) {
        return (
          <Redirect
            to={getSignalFollowTargetPath(
              parseInt(signalId),
              parseInt(subGroupId),
              target
            )}
          />
        );
      } else {
        return <RedirectWithQuery to={SECURITY_SETTINGS_PATH} />;
      }
    } else {
      return <Redirect to="/" />;
    }
  }
};

let didQuickOnboardingRedirect = false;
// Whether a View Signal was opened on an earlier visit, read once per app
// load: the mark this visit leaves must not count as a second tap on the
// next render (it did, so the guide covered the first signal too).
let viewSignalSeenBefore: boolean | undefined;

const checkQuickOnboardingRedirect = (
  user: AuthUser | undefined,
  location: Location,
  testPath: (path: string, exact?: boolean) => boolean,
  getHomePath: () => string,
  telegramStartValue?: TelegramWebappQueryData,
  workspace?: WorkspaceType
) => {
  const isQuickOnboardingPath = testPath(QUICK_ONBOARDING_PATH);
  // The start parameter leaves the URL once its page opens; the session
  // keeps the channel button's path.
  const channelEntryPath = getChannelEntryPath();
  const isChannel =
    telegramStartValue?.parameters?.source === "channel" || !!channelEntryPath;
  const startPath = telegramStartValue?.path ?? channelEntryPath;
  const isViewSignal =
    isChannel &&
    !!startPath &&
    !!matchPath(startPath, { path: SIGNAL_EXTRA_DETAILS_PATH });
  viewSignalSeenBefore ??=
    localStorageService.getValue(TELEGRAM_VIEW_SIGNAL_KEY) === "true";
  const isViewSignalSecondClick = viewSignalSeenBefore;
  const quickOnboardingViewed =
    localStorageService.getValue(QUICK_ONBOARDING_KEY) === "true";
  const shouldShowQuickOnboarding =
    workspace === WorkspaceType.USER &&
    // Still onboarding but arrived from a channel post's button: the same
    // quick onboarding as before.
    (!user?.is_onboarding || isChannelEntry()) &&
    !user?.has_clients_connected &&
    !user?.is_sub_user &&
    // Connecting and creating a bot need a subscription first.
    !user?.isExpired();

  const showQuickOnboarding =
    shouldShowQuickOnboarding &&
    !isQuickOnboardingPath &&
    !quickOnboardingViewed &&
    (!isChannel || !isViewSignal || isViewSignalSecondClick);

  if (isViewSignal && !isViewSignalSecondClick && shouldShowQuickOnboarding) {
    localStorageService.setValue(TELEGRAM_VIEW_SIGNAL_KEY, true);
    return null;
  } else if (showQuickOnboarding && !didQuickOnboardingRedirect) {
    const pathname =
      testPath("/", true) || location.pathname === "//"
        ? getHomePath()
        : location.pathname;
    didQuickOnboardingRedirect = true;
    return <RedirectWithQuery to={getQuickOnboardingRoute(pathname)} />;
  }
};

export const checkAppRedirects = (
  user: AuthUser,
  location: Location,
  query: URLSearchParams,
  getValidWorkspacesByPath: (pathname: string) => WorkspaceType[],
  getHomePath: () => string,
  telegramStartValue?: TelegramWebappQueryData,
  workspace?: WorkspaceType
) => {
  const testPath = (path: string, exact = true) =>
    !!matchPath(location.pathname, {
      path,
      exact,
      strict: false,
    });

  for (let route of Object.keys(PATH_TO_FEATURE_FLAG)) {
    const featureFlag = PATH_TO_FEATURE_FLAG[route];
    if (testPath(route, false) && !user.hasFeatureFlag(featureFlag)) {
      return <Redirect to="/" />;
    }
  }

  if (
    testPath(CREATE_SIGNAL_PATH, false) &&
    !user.isAssetManager() &&
    workspace !== WorkspaceType.ADMIN
  ) {
    return <Redirect to="/" />;
  }

  const externalLinkRedirect = checkExternalLinksRedirects(
    user,
    location,
    query
  );
  if (externalLinkRedirect) return externalLinkRedirect;
  const connectTelegramRedirect = checkConnectTelegramRedirects(
    user,
    testPath,
    query
  );
  if (connectTelegramRedirect) return connectTelegramRedirect;
  const onboardingRedirect = checkOnboardingRedirects(
    user,
    testPath,
    query,
    workspace
  );
  if (onboardingRedirect) return onboardingRedirect;
  const whopRedirects = checkWhopRedirects(user, testPath, workspace);
  if (whopRedirects) return whopRedirects;
  const quickOnboardingRedirect = checkQuickOnboardingRedirect(
    user,
    location,
    testPath,
    getHomePath,
    telegramStartValue,
    workspace
  );
  if (quickOnboardingRedirect) return quickOnboardingRedirect;

  const paymentStatusPaths = [
    BASE_CANCELLED_AGREEMENT_PATH,
    BASE_PROCESSED_AGREEMENT_PATH,
    BASE_FAILED_AGREEMENT_PATH,
    BASE_PAID_AGREEMENT_PATH,
  ];
  for (let route of paymentStatusPaths) {
    if (testPath(route, false)) {
      return (
        <RedirectWithQuery
          to={
            BASE_BILLING_SETTINGS_PATH +
            location.pathname +
            (location.search || "")
          }
        />
      );
    }
  }

  if (
    !user.is_onboarding &&
    !ONBOARDING_PATHS.some((path) => testPath(path, false))
  ) {
    const nextPath = query.get(NEXT_URL_QUERY_PARAM);
    if (nextPath) {
      return (
        <RedirectWithQuery
          queryKeysToDelete={[NEXT_URL_QUERY_PARAM]}
          to={nextPath}
        />
      );
    }

    if (
      !user.is_whop_user &&
      !getValidWorkspacesByPath(location.pathname).length
    ) {
      return <Redirect to="/" />;
    }
  }
};

// Where a Discord signal button leads once the Discord account is connected:
// the signal details (View Signal, also for links without a target) or the
// new signals bot form for the channel (Set Auto Trading).
export const getSignalFollowTargetPath = (
  signalId: number,
  subGroupId: number,
  target?: string
) => {
  if (target === SIGNAL_TARGET_AUTO_TRADING) {
    return appendSubGroupId(
      getBotFormRoute(BotType.SIGNAL, BotFormType.CREATE),
      subGroupId
    );
  }
  return appendSubGroupId(
    getSignalExtraDetailsRoute(
      SIGNAL_BOTS_SIGNALS_PATH,
      signalId,
      SIGNAL_EXTRA_DETAILS_TABS.GENERAL,
      false
    ),
    subGroupId
  );
};

export const getSignalFollowData = (query: URLSearchParams) => {
  const decodeValue = (value?: string | null) => atob(value || "");
  const actionAndSignalId = decodeValue(query.get(SIGNAL_URL_KEY));
  const subGroupId = decodeValue(query.get(SUB_GROUP_URL_KEY));
  const ref = decodeValue(query.get(REGISTRATION_REF_URL_KEY));
  const target = decodeValue(query.get(SIGNAL_TARGET_URL_KEY));
  const actionAndSignalIdMatch = actionAndSignalId?.match(
    new RegExp("(\\w*)-(\\d+)")
  );
  const signalId = actionAndSignalIdMatch?.[2];
  return {
    actionAndSignalId,
    subGroupId,
    signalId,
    ref,
    target,
  };
};

export const getCreateChannelConfigurationsPath = () => {
  return getBotFormRoute(BotType.SIGNAL, BotFormType.CREATE, undefined, true);
};

export const getRouteWithExchange = (primaryExchange: string) => {
  const updatedQuery = new URLSearchParams();
  updatedQuery.set("exchange", primaryExchange);
  return `${location.pathname}?${updatedQuery.toString()}`;
};

export const getTelegramWebAppPath = (botLink: string, path?: string) => {
  const updatedQuery = new URLSearchParams();
  let param = "";
  if (path) {
    try {
      param = btoa(JSON.stringify({ path }));
    } catch {}
  }
  updatedQuery.set("startapp", param);
  return `${botLink}/cornix?${updatedQuery.toString()}`;
};

export const useQueryGroupId = () => {
  const query = useQuery();
  const rawData = query.get(FILTER_TYPES_DATA[FilterType.GROUPS].queryKey);
  if (rawData) {
    try {
      return JSON.parse(rawData)?.[0];
    } catch {}
  }
};

export const checkAppExternalRedirects = (
  location: Location,
  query: URLSearchParams
) => {
  const clientToken = query.get("client_token");

  if (checkQuickConnectRedirects(location.pathname) && clientToken) {
    return <RedirectWithQuery to={ONBOARDING_CONNECT_EXCHANGE_PATH} />;
  }
  if (
    matchPath(location.pathname, {
      path: OAUTH_LOGIN_PATH,
      exact: true,
      strict: false,
    })
  ) {
    return <RedirectWithQuery to={LOGIN_PATH} />;
  }
};
